import { useEffect, useState } from "react";
import api, { formatMoney, formatDate, daysUntil, urgencyClass } from "@/lib/api";
import { useCompany } from "@/lib/company";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash, PencilSimple, FileArrowDown, CheckCircle, Prohibit, ArrowUUpLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import MoneyInput from "@/components/MoneyInput";

const EVRAK_TURU = ["Kendi Çekim", "Müşteri Çeki"];
const DURUM = ["Portföyde", "Takasta", "Ciro Edildi"];
const ODEME_DURUMU = ["Ödenmedi", "Ödendi", "İade Edildi"];
const CURRENCIES = ["TRY", "USD", "EUR"];

const empty = {
  company_id: "", evrak_turu: "Kendi Çekim", durum: "Portföyde", odeme_durumu: "Ödenmedi",
  vade_tarihi: "", evrak_no: "", tutar: 0, currency: "TRY", ticari_unvan: "",
  banka: "", sube: "", verilen: "", odeme_tarihi: "", notes: "",
};

const StatusIcon = ({ odeme_durumu, kalan }) => {
  if (odeme_durumu === "Ödendi") {
    return (
      <div className="flex items-center gap-1" title="Ödendi">
        <CheckCircle size={22} weight="fill" className="urgency-green" />
      </div>
    );
  }
  if (odeme_durumu === "İade Edildi") {
    return (
      <div className="flex items-center gap-1" title="İade Edildi">
        <ArrowUUpLeft size={22} weight="fill" className="urgency-yellow" />
      </div>
    );
  }
  // Ödenmedi — kalan gün badge veya (kalan <= 0 ise) yasak işareti
  if (kalan !== null && kalan <= 0) {
    return (
      <div className="flex items-center gap-1" title="Vadesi geçmiş, ödenmemiş">
        <Prohibit size={22} weight="fill" className="urgency-red" />
        <span className="text-xs urgency-red font-semibold">{kalan}g</span>
      </div>
    );
  }
  return <Badge className={urgencyClass(kalan)}>{kalan} gün</Badge>;
};

export default function ChecksPage() {
  const { companies, selectedCompany } = useCompany();
  const [checks, setChecks] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const q = selectedCompany === "all" ? "" : `?company_id=${selectedCompany}`;
    const { data } = await api.get(`/checks${q}`);
    setChecks(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedCompany]);

  const openNew = () => {
    setForm({ ...empty, company_id: selectedCompany !== "all" ? selectedCompany : companies[0]?.id || "" });
    setEditingId(null);
    setDialog(true);
  };
  const openEdit = (c) => { setForm({ ...c }); setEditingId(c.id); setDialog(true); };

  const save = async () => {
    if (!form.company_id) { toast.error("Şirket seçin"); return; }
    // Ödendi seçilirse ödeme tarihini otomatik bugün yap
    const payload = { ...form };
    if (payload.odeme_durumu === "Ödendi" && !payload.odeme_tarihi) {
      payload.odeme_tarihi = new Date().toISOString().slice(0, 10);
    }
    try {
      if (editingId) await api.put(`/checks/${editingId}`, payload);
      else await api.post("/checks", payload);
      toast.success("Kaydedildi");
      setDialog(false); load();
    } catch { toast.error("Kayıt hatası"); }
  };

  const remove = async (id) => {
    if (!confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/checks/${id}`); toast.success("Silindi"); load();
  };

  const markPaid = async (c) => {
    const payload = { ...c, odeme_durumu: "Ödendi", odeme_tarihi: new Date().toISOString().slice(0, 10) };
    await api.put(`/checks/${c.id}`, payload);
    toast.success("Çek ödendi olarak işaretlendi");
    load();
  };

  const exportPDF = async () => {
    const { jsPDF } = await import("jspdf");
    const autoTable = (await import("jspdf-autotable")).default;
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text("Cek Takip Raporu", 14, 15);
    doc.setFontSize(9);
    doc.text(new Date().toLocaleString("tr-TR"), 14, 21);
    autoTable(doc, {
      startY: 25,
      head: [["Sirket", "Turu", "Durum", "Odeme", "Vade", "Evrak No", "Tutar", "Firma", "Banka"]],
      body: filtered.map((c) => [
        companies.find((cc) => cc.id === c.company_id)?.name || "",
        c.evrak_turu, c.durum, c.odeme_durumu, formatDate(c.vade_tarihi), c.evrak_no,
        formatMoney(c.tutar, c.currency), c.ticari_unvan, c.banka,
      ]),
      styles: { fontSize: 8 },
    });
    doc.save(`cekler-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const filtered = checks
    .filter((c) => {
      if (filter === "all") return c.odeme_durumu !== "Ödendi";
      if (filter === "kendi") return c.evrak_turu === "Kendi Çekim" && c.odeme_durumu !== "Ödendi";
      if (filter === "musteri") return c.evrak_turu === "Müşteri Çeki" && c.odeme_durumu !== "Ödendi";
      if (filter === "takasta") return c.durum === "Takasta";
      if (filter === "ciro") return c.durum === "Ciro Edildi";
      if (filter === "odenen") return c.odeme_durumu === "Ödendi";
      if (filter === "iade") return c.odeme_durumu === "İade Edildi";
      return true;
    })
    .filter((c) => {
      if (!search) return true;
      const s = search.toLowerCase();
      return [c.evrak_no, c.ticari_unvan, c.banka, c.sube].some(
        (v) => v && v.toLowerCase().includes(s)
      );
    })
    .sort((a, b) => new Date(a.vade_tarihi) - new Date(b.vade_tarihi));

  const total = filtered.reduce((acc, c) => acc + (c.currency === "TRY" ? c.tutar : 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Çekler</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Kendi çekim / müşteri çeki · durum (portföyde, takasta, ciro edildi) · ödeme durumu.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportPDF} data-testid="export-pdf-btn"><FileArrowDown size={16} className="mr-2" /> PDF</Button>
          <Button onClick={openNew} data-testid="add-check-btn"><Plus size={16} className="mr-2" /> Yeni Çek</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            <TabsTrigger value="all" data-testid="filter-all">Aktif</TabsTrigger>
            <TabsTrigger value="kendi" data-testid="filter-kendi">Kendi</TabsTrigger>
            <TabsTrigger value="musteri" data-testid="filter-musteri">Müşteri</TabsTrigger>
            <TabsTrigger value="takasta" data-testid="filter-takasta">Takasta</TabsTrigger>
            <TabsTrigger value="ciro" data-testid="filter-ciro">Ciro</TabsTrigger>
            <TabsTrigger value="odenen" data-testid="filter-odenen">Ödenen</TabsTrigger>
            <TabsTrigger value="iade" data-testid="filter-iade">İade</TabsTrigger>
          </TabsList>
        </Tabs>
        <Input data-testid="search-input" placeholder="Firma, evrak no, banka ara…" value={search}
          onChange={(e) => setSearch(e.target.value)} className="max-w-sm" />
        <div className="ml-auto text-sm text-muted-foreground">
          Toplam (TRY): <span className="font-semibold text-foreground tabular-nums">{formatMoney(total)}</span>
        </div>
      </div>

      <Card className="border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Durum</TableHead>
                <TableHead>Vade</TableHead>
                <TableHead>Kimin / Nerede</TableHead>
                <TableHead>Evrak No</TableHead>
                <TableHead>Firma</TableHead>
                <TableHead>Banka</TableHead>
                <TableHead className="text-right">Tutar</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Çek bulunmuyor.</TableCell></TableRow>
              )}
              {filtered.map((c) => {
                const d = daysUntil(c.vade_tarihi);
                return (
                  <TableRow key={c.id} data-testid={`check-row-${c.id}`}>
                    <TableCell><StatusIcon odeme_durumu={c.odeme_durumu} kalan={d} /></TableCell>
                    <TableCell className="tabular-nums text-sm">
                      {formatDate(c.vade_tarihi)}
                      {c.odeme_durumu === "Ödendi" && c.odeme_tarihi && (
                        <div className="text-[10px] text-muted-foreground">Ödendi: {formatDate(c.odeme_tarihi)}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">{c.evrak_turu}</div>
                      <div className="text-xs text-muted-foreground">{c.durum}</div>
                    </TableCell>
                    <TableCell className="tabular-nums text-sm">{c.evrak_no}</TableCell>
                    <TableCell className="text-sm">{c.ticari_unvan}</TableCell>
                    <TableCell className="text-sm">
                      <div>{c.banka}</div>
                      <div className="text-xs text-muted-foreground">{c.sube}</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{formatMoney(c.tutar, c.currency)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      {c.odeme_durumu === "Ödenmedi" && (
                        <Button size="sm" variant="ghost" onClick={() => markPaid(c)} title="Ödendi olarak işaretle" data-testid={`mark-paid-${c.id}`}>
                          <CheckCircle size={16} className="urgency-green" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-${c.id}`}><PencilSimple size={15} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(c.id)} data-testid={`delete-${c.id}`}><Trash size={15} className="text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingId ? "Çeki Düzenle" : "Yeni Çek Ekle"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Şirket</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="form-company"><SelectValue placeholder="Seç..." /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kimin Çeki</Label>
              <Select value={form.evrak_turu} onValueChange={(v) => setForm({ ...form, evrak_turu: v })}>
                <SelectTrigger data-testid="form-turu"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EVRAK_TURU.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Durumu (Nerede)</Label>
              <Select value={form.durum} onValueChange={(v) => setForm({ ...form, durum: v })}>
                <SelectTrigger data-testid="form-durum"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DURUM.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ödeme Durumu</Label>
              <Select value={form.odeme_durumu} onValueChange={(v) => setForm({ ...form, odeme_durumu: v })}>
                <SelectTrigger data-testid="form-odeme-durumu"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ODEME_DURUMU.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vade Tarihi</Label>
              <Input data-testid="form-vade" type="date" value={form.vade_tarihi}
                onChange={(e) => setForm({ ...form, vade_tarihi: e.target.value })} />
            </div>
            <div>
              <Label>Ödeme Tarihi (varsa)</Label>
              <Input type="date" value={form.odeme_tarihi}
                onChange={(e) => setForm({ ...form, odeme_tarihi: e.target.value })} />
            </div>
            <div>
              <Label>Evrak No</Label>
              <Input data-testid="form-evrak-no" value={form.evrak_no} onChange={(e) => setForm({ ...form, evrak_no: e.target.value })} />
            </div>
            <div>
              <Label>Tutar</Label>
              <div className="flex gap-2">
                <MoneyInput data-testid="form-tutar" value={form.tutar}
                  onChange={(n) => setForm({ ...form, tutar: n })} />
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="col-span-2">
              <Label>Ticari Ünvan / Firma</Label>
              <Input value={form.ticari_unvan} onChange={(e) => setForm({ ...form, ticari_unvan: e.target.value })} />
            </div>
            <div><Label>Banka</Label><Input value={form.banka} onChange={(e) => setForm({ ...form, banka: e.target.value })} /></div>
            <div><Label>Şube</Label><Input value={form.sube} onChange={(e) => setForm({ ...form, sube: e.target.value })} /></div>
            <div className="col-span-2">
              <Label>Verilen (Ciro/Takas için)</Label>
              <Input value={form.verilen} onChange={(e) => setForm({ ...form, verilen: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>İptal</Button>
            <Button onClick={save} data-testid="form-save-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
